
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  ChatInterface,
  ChatRepositoryInterface,
  ProfileMapInterface,
  ChatReviewFormDataInterface, FilterRequestData,
} from '@/services/QC-V2/chat/interfaces';
import { CreateTagInterface, TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import { ScoreTypeInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface } from '@/services/QC-V2/score-grade/interfaces';
import ChatComments from '@/components/qa/chat/ChatComments.vue';
import FeedbackDialog, { FeedbackDialogDataInterface } from '@/components/dialogs/qc-v2/feedback/FeedbackDialog.vue';
import {
  ChatFeedbackCreateInterface,
  FeedbackRepositoryInterface,
  FeedbackStatus,
} from '@/services/QC-V2/feedback/interfaces';
import ChatEscalateDialog from '@/components/dialogs/qc-v2/escalation/ChatEscalateDialog.vue';
import { ChatEscalationCreateData, EscalationRepositoryInterface } from '@/services/QC-V2/escalation/interfaces';
import { UserBaseInfoInterface } from '@/services/Users/Interfaces';

interface ProfilesSelectInterface {
  id: string,
  name: string,
}

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    ChatComments,
    FeedbackDialog,
    EscalateDialog: ChatEscalateDialog,
  },
  inject: [
    'chatRepositoryV2',
    'scoreTypeRepositoryV2',
    'scoreGradeRepositoryV2',
    'tagRepositoryV2',
    'feedbackRepositoryV2',
    'escalationRepositoryV2',
    'userRepository',
  ],
})
export default class ChatReview extends Vue {
  public $refs!: { 'review-comments': HTMLFormElement };
  @Prop() private chat!: ChatInterface;
  @Prop() private scoreTypes!: ScoreTypeInterface[];
  @Prop() private scoreGrades!: ScoreGradeInterface[];
  @Prop() private tags!: TagInterface[];
  @Prop() private profiles!: ProfilesSelectInterface[];
  @Prop() private profilesMap!: ProfileMapInterface;
  @Prop({
    required: false,
    default: () => [],
  }) private users!: UserBaseInfoInterface[];

  @Prop({
    required: false,
    default: () => ({}),
  })
  private filter!: FilterRequestData | undefined;

  private loading: boolean = true;

  private chatRepositoryV2!: ChatRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;

  private feedbackRepositoryV2!: FeedbackRepositoryInterface;
  private escalationRepositoryV2!: EscalationRepositoryInterface;

  private commentProfiles: ProfileMapInterface = {};

  private searchTag: string = '';
  private selectedTags: TagInterface[] = [];

  private formValid: boolean = false;

  private reviewSubmitted: boolean = false;

  private selectedUser: string = 'chat';

  private formData: { [userId: string]: ChatReviewFormDataInterface } = {};

  private dataForFeedbackDialog: FeedbackDialogDataInterface | undefined = undefined;
  private feedbackFormValid: boolean = false;
  private feedbackDialogVisible: boolean = false;
  private draftValid: boolean = false;
  private escalateFormValid: boolean = false;
  private escalateDialogVisible: boolean = false;

  private async created(): Promise<void> {
    await this.initialize();
  }

  @Watch('chat')
  private async initialize(): Promise<void> {
    this.loading = true;
    this.initializeFormData();
    this.extractReviewData();
    this.watchAgentFilter();
    this.loading = false;
  }

  private initializeFormData(): void {
    this.searchTag = '';
    this.selectedTags = [];
    this.reviewSubmitted = false;
    this.selectedUser = 'chat';
    this.formData = {
      chat: {
        chatId: this.chat.id,
        reviewerId: this.$store.getters.id,
        isDraft: false,
        reviewedId: 'chat',
        scores: {},
        tags: [],
        comments: [],
      },
    };

    this.profiles.forEach((profile) => {
      this.formData[profile.id] = {
        chatId: this.chat.id,
        reviewerId: this.$store.getters.id,
        isDraft: false,
        reviewedId: profile.id,
        scores: {},
        tags: [],
        comments: [],
      };
    });
  }

  private addScore(scoreTypeId: number, scoreGradeId: number): void {
    this.formData[this.selectedUser].scores[scoreTypeId] = scoreGradeId;
    this.validateForm();
  }

  private removeScore(scoreTypeId: number): void {
    delete this.formData[this.selectedUser].scores[scoreTypeId];
    this.validateForm();
  }

  private async review(): Promise<void> {
    if (this.reviewHasUnsentComment()) {
      this.$infoDialog.show('You have unsent comment!');
      return;
    }

    try {
      if (this.formData[this.selectedUser].reviewedId === 'chat') {
        this.reviewSubmitted = true;
        await this.addChatTags();
        this.reviewSubmitted = false;
        return;
      }
      const existingReviewedIds = this.chat.reviews
        .filter((review) => !review.isDraft)
        .map((review) => review.reviewedId);

      const reviewData = Object.values(this.formData)
        .filter((data) => data.reviewedId !== 'chat'
          && !existingReviewedIds.includes(data.reviewedId)
          && Object.values(data.scores).length);

      if (!reviewData.length) {
        this.$errorDialog.show('Invalid review data!');
        return;
      }

      await this.chatRepositoryV2.reviewChat(reviewData);
      this.addReviews(reviewData);

      this.reviewSubmitted = true;

      this.$successDialog.show('Review created successfully');
      this.$emit('chat-reviewed', this.chat.id);
      this.initializeFormData();
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private async saveAsDraft(): Promise<void> {
    this.formData[this.selectedUser].isDraft = true;
    await this.review();
  }

  private validateForm(): void {
    if (this.formData[this.selectedUser].reviewedId === 'chat' && this.formData.chat.tags.length) {
      this.formValid = true;
      this.feedbackFormValid = false;
      return;
    }

    const valid = (Object.values(this.formData[this.selectedUser].scores).every((score) => !!score))
      && (Object.keys(this.formData[this.selectedUser].scores).length === this.scoreTypes.length)
      && (!!this.formData[this.selectedUser].reviewedId);

    this.formValid = valid && ((!!this.formData[this.selectedUser].comments.length && !!this.formData[this.selectedUser].tags.length)
      || (!this.formData[this.selectedUser].comments.length && !this.formData[this.selectedUser].tags.length));

    this.feedbackFormValid = this.formValid
      && !this.chat.feedbacks.find((feedback) => feedback.feedbackTo === this.selectedUser)
      && !!this.formData[this.selectedUser].tags.length;

    this.draftValid = valid && !this.chat.reviews.find((review) => review.reviewedId === this.selectedUser);

    this.escalateFormValid = Object.values(this.formData)
        .filter((data) => data.reviewedId !== 'chat')
        .every((data) => Object.values(data.scores).filter((score) => !!score).length === this.scoreTypes.length)
      || Object.values(this.formData)
        .every((data) => Object.values(data.scores).filter((score) => !!score).length === 0);

    this.getDataForFeedbackDialog();
  }

  private async addChatTags(): Promise<void> {
    if (this.formData[this.selectedUser].reviewedId !== 'chat') {
      return;
    }

    try {
      await this.tagRepositoryV2.addChatTags(this.chat.id, this.formData[this.selectedUser].tags.map((tag) => tag.id));
      this.$successDialog.show('Tags added to chat successfully');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private async createTag(): Promise<void> {
    if (!this.searchTag) {
      return;
    }

    const data = {
      label: this.searchTag,
      color: '#808080FF',
    } as CreateTagInterface;

    const newTag = await this.tagRepositoryV2.createTag(data);

    this.tags.push(newTag);
    this.formData[this.selectedUser].tags.push(newTag);
  }

  private async createFeedback(): Promise<void> {
    if (this.reviewHasUnsentComment()) {
      this.$infoDialog.show('You have unsent comment!');
      return;
    }

    const feedbackData: ChatFeedbackCreateInterface = {
      chatId: this.chat.id,
      feedbackTo: this.formData[this.selectedUser].reviewedId,
      feedbackFrom: this.$store.getters.id,
      status: FeedbackStatus.SENT,
      isDraft: false,
    };

    try {
      this.formData[this.selectedUser].isDraft = false;
      await this.feedbackRepositoryV2.createChatFeedback(feedbackData, this.formData[this.selectedUser]);
      this.$emit('chat-reviewed', this.chat.id);
      this.$successDialog.show('Feedback created Successfully');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private async createEscalation(data: ChatEscalationCreateData): Promise<void> {
    if (this.chat.escalation) {
      this.$successDialog.show('Chat already escalated');
      return;
    }

    if (this.reviewHasUnsentComment()) {
      this.$infoDialog.show('You have unsent comment!');
      return;
    }

    try {
      let reviewsData;
      const hasReviews = Object.values(this.formData)
        .filter((reviewData) => reviewData.reviewedId !== 'chat')
        .every((reviewData) => Object.values(reviewData.scores)
          .filter((score) => !!score).length === this.scoreTypes.length);

      if (hasReviews) {
        reviewsData = Object.values(this.formData)
          .filter((reviewData) => reviewData.reviewedId !== 'chat'
            && !this.chat.reviews.find((review) => !review.deletedAt && review.reviewedId === reviewData.reviewedId));
      }


      data.agentsIds = this.profiles.filter((profile) => profile.id !== 'chat').map((profile) => profile.id);
      await this.escalationRepositoryV2.createChatEscalation(data, reviewsData);
      this.$emit('chat-reviewed', this.chat.id);
      this.$successDialog.show('Escalation created Successfully');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private getDataForFeedbackDialog(): void {
    if (this.formData[this.selectedUser].reviewedId === 'chat') {
      this.dataForFeedbackDialog = undefined;
      return;
    }
    const user = this.profiles.find((profile) => profile.id === this.formData[this.selectedUser].reviewedId);

    if (!user) {
      this.dataForFeedbackDialog = undefined;
      return;
    }
    const scores: string[] = [];
    Object.keys(this.formData[this.selectedUser].scores).forEach((scoreTypeId) => {
      const type = this.scoreTypes.find((st) => st.id === Number(scoreTypeId));
      const grade = this.scoreGrades
        .find((sg) => sg.id === Number(this.formData[this.selectedUser].scores[Number(scoreTypeId)]));

      if (!type || !grade) {
        return;
      }

      scores.push(`${type.label} - ${grade.label}`);
    });

    this.dataForFeedbackDialog = {
      user,
      scores,
      comments: this.formData[this.selectedUser].comments,
      tags: this.selectedTags,
      usersMap: this.commentProfiles,
    };
  }

  private assignCommentsProfiles(profiles: ProfileMapInterface): void {
    this.commentProfiles = profiles;
    this.getDataForFeedbackDialog();
  }

  private extractReviewData(): void {
    this.chat.tags.forEach((chatTag) => {
      const tag = this.tags.find((t) => t.id === chatTag.tagId);
      if (!tag) {
        return;
      }

      this.formData.chat.tags.push(tag);
    });

    this.chat.reviews.forEach((review) => {
      this.formData[review.reviewedId].comments = review.comments.map((comment) => ({
        ...comment,
        addToDossier: true,
      }));

      review.tags.forEach((reviewTag) => {
        const tag = this.tags.find((t) => t.id === reviewTag.tagId);
        if (!tag) {
          return;
        }

        this.formData[review.reviewedId].tags.push(tag);
      });

      review.scores.forEach((score) => {
        this.formData[review.reviewedId].scores[score.scoreTypeId] = score.scoreGradeId;
      });
    });

  }

  @Watch('filter')
  private watchAgentFilter(): void {
    if (!this.filter || !this.filter['agent.email']) {
      this.setAgentForReview();
      return;
    }

    this.setAgentForReview(String(this.filter['agent.email']));
  }

  private setAgentForReview(email?: string): void {
    if (!this.profiles[1]) {
      return;
    }

    this.selectedUser = this.profiles[1].id;

    if (!email) {
      return;
    }

    const user = this.users.find((user) => user.email === email);
    if (!user) {
      return;
    }

    this.selectedUser = user.id;
  }

  private addReviews(reviewsData: ChatReviewFormDataInterface[]): void {
    reviewsData.forEach((reviewData) => {
      this.chat.reviews.push({
        id: 0,
        reviewedId: reviewData.reviewedId,
        reviewerId: reviewData.reviewerId,
        comments: reviewData.comments.map((c) => ({
          id: 0,
          author: c.author,
          text: c.text,
          rate: c.rate,
          timestamp: c.timestamp,
        })),
        isDraft: reviewData.isDraft,
        scores: Object.keys(reviewData.scores).map((scoreTypeId) => ({
          id: 0,
          scoreTypeId: Number(scoreTypeId),
          scoreGradeId: reviewData.scores[Number(scoreTypeId)],
        })),
        deletedAt: undefined,
        tags: reviewData.tags.map((tag) => ({ tagId: tag.id })),
        timestamp: new Date(),
      });
    });
  }

  private reviewHasUnsentComment(): boolean {
    return this.$refs['review-comments']['chatComment'] && this.$refs['review-comments']['chatComment'].trim() !== '';
  }
}
